

import { Type } from "@google/genai";
import { Company, Project, EmailDraft, Interaction, AIProvider, CostMode, GeminiModel } from '../types';
import { MemoryService } from './memoryService';
import { SettingsService } from './settingsService';
import { GeminiService } from './geminiService';
import { OpenRouterService } from './openRouterService';
import { LocalLLMService } from './localLLMService';

export type EmailTone = 'professional' | 'casual';
const MAX_EMAIL_WORDS = 140;

const normalizeSpacing = (value: string) =>
  value
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/([.,!?])(?=[A-ZÀ-ÖØ-Ý])/g, '$1 ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const extractFirstEmail = (value: string) => {
  const match = value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return match?.[0] || '';
};

const isEmailAddress = (value: string) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test((value || '').trim());

const buildSenderEmail = (project: Project) =>
  normalizeSpacing(project.senderEmail || extractFirstEmail(project.senderContactBlock || '') || extractFirstEmail(project.description || ''));

const buildContactBlock = (project: Project) =>
  normalizeSpacing(project.senderContactBlock || '').replace(/\n{3,}/g, '\n\n');

const buildRecipientAddress = (draftTo: string, company: Company) => {
  const normalizedDraftTo = normalizeSpacing(draftTo || '');
  if (isEmailAddress(normalizedDraftTo)) {
    return normalizedDraftTo;
  }

  if (isEmailAddress(company.email || '')) {
    return normalizeSpacing(company.email || '');
  }

  return normalizedDraftTo;
};

const formatEmailBody = (rawBody: string) => {
  const cleaned = normalizeSpacing(rawBody);
  if (!cleaned) return cleaned;

  let body = cleaned;

  body = body.replace(
    /^(Beste\s+[^,\n]+,|Geachte\s+[^,\n]+,|Hallo\s+[^,\n]+,|Hoi\s+[^,\n]+,)/i,
    (match) => `${match}\n\n`
  );

  body = body.replace(
    /\s+(Zou(?:\s+u|\s+je)?\s+openstaan\b|Sta(?:at| je)\s+u?\s+open\b|Lijkt\s+het\s+u?\b|Wanneer\s+schikt\b|Past\s+een\s+kort\s+gesprek\b|Mag\s+ik\s+voorstellen\b|Graag\s+bespreek\s+ik\b)/gi,
    '\n\n$1'
  );

  body = body.replace(
    /\s+(Met\s+vriendelijke\s+groet,?|Vriendelijke\s+groet,?|Hartelijke\s+groet,?|Groet,?)/gi,
    '\n\n$1'
  );

  const lines = body.split('\n').map((line) => line.trim()).filter(Boolean);
  const formattedLines: string[] = [];

  for (const line of lines) {
    const isGreeting = /^(Beste|Geachte|Hallo|Hoi)\b/i.test(line);
    const isClosing = /^(Met vriendelijke groet|Vriendelijke groet|Hartelijke groet|Groet)\b/i.test(line);

    if (isGreeting || isClosing) {
      formattedLines.push(line);
      formattedLines.push('');
      continue;
    }

    const sentences = line
      .split(/(?<=[.!?])\s+(?=[A-ZÀ-ÖØ-Ý])/)
      .map((sentence) => sentence.trim())
      .filter(Boolean);

    if (sentences.length <= 2) {
      formattedLines.push(sentences.join(' '));
      formattedLines.push('');
      continue;
    }

    for (let index = 0; index < sentences.length; index += 2) {
      formattedLines.push(sentences.slice(index, index + 2).join(' '));
      formattedLines.push('');
    }
  }

  return formattedLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

const trimToWordLimit = (paragraphs: string[], limit: number) => {
  const words = paragraphs.join(' ').trim().split(/\s+/).filter(Boolean);
  if (words.length <= limit) {
    return paragraphs;
  }

  const trimmed = words.slice(0, limit).join(' ').replace(/\s+([,.!?;:])/g, '$1');
  return [trimmed.replace(/[,:;]$/, '').trim() + '.'];
};

const enforceEmailGuidelines = (body: string, project: Project, company: Company) => {
  const greeting = company.contactName ? `Beste ${company.contactName},` : `Beste ${company.name},`;
  const signatureName = project.name.trim() || 'BDAT';
  const signatureLines = [signatureName];
  const formatted = formatEmailBody(body)
    .replace(/\[(uw naam|naam|bedrijf|functie|verantwoordelijke|contactpersoon)\]/gi, signatureName)
    .replace(/(Met vriendelijke groet,?\s*)(?:\n+.+)?$/i, '')
    .trim();

  let paragraphs = formatted.split(/\n\s*\n/).map((paragraph) => paragraph.trim()).filter(Boolean);

  if (paragraphs.length === 0 || !/^(Beste|Geachte|Hallo|Hoi)\b/i.test(paragraphs[0])) {
    paragraphs.unshift(greeting);
  } else {
    paragraphs[0] = greeting;
  }

  const contentParagraphs = paragraphs.slice(1).filter((paragraph) => !/^(Met vriendelijke groet|Vriendelijke groet|Hartelijke groet|Groet)\b/i.test(paragraph));
  const ctaIndex = contentParagraphs.findIndex((paragraph) => /\?|openstaan|gesprek|kennismaking|afstemmen|schikt/i.test(paragraph));
  const ctaParagraph = ctaIndex > -1
    ? contentParagraphs.splice(ctaIndex, 1)[0]
    : `Zou een kort gesprek van 15 minuten voor u passen om dit af te stemmen?`;

  const compactContent = trimToWordLimit(contentParagraphs, Math.max(60, MAX_EMAIL_WORDS - 22)).slice(0, 2);
  const finalParagraphs = [
    greeting,
    ...compactContent,
    ctaParagraph,
    'Met vriendelijke groet,',
    ...signatureLines,
  ];

  const normalizedBody = finalParagraphs.join('\n\n');
  const finalWords = normalizedBody.split(/\s+/).filter(Boolean);

  if (finalWords.length <= MAX_EMAIL_WORDS) {
    return normalizedBody;
  }

  const overLimitContent = trimToWordLimit(compactContent, Math.max(35, MAX_EMAIL_WORDS - 18));
  return [
    greeting,
    ...overLimitContent,
    ctaParagraph,
    'Met vriendelijke groet,',
    ...signatureLines,
  ].join('\n\n');
};

const normalizeEmailDraft = (
  draft: EmailDraft,
  project: Project,
  company: Company,
  type: 'initial' | 'followup',
  tone: EmailTone
): EmailDraft => ({
  to: buildRecipientAddress(draft.to || company.email || '', company),
  from: normalizeSpacing(draft.from || buildSenderEmail(project)),
  subject: normalizeSpacing(draft.subject || `Introductie voor ${company.name}`),
  body: enforceEmailGuidelines(draft.body || '', project, company),
  contactBlock: normalizeSpacing(draft.contactBlock || buildContactBlock(project)),
  meta: {
    language: draft.meta?.language || 'nl',
    tone: draft.meta?.tone || tone,
    purpose: draft.meta?.purpose || type,
  },
});

export class EmailAgentService {
  static async draftEmail(
    project: Project,
    company: Company,
    type: 'initial' | 'followup',
    tone: EmailTone = 'professional'
  ): Promise<EmailDraft> {
    const settings = SettingsService.getSettings();
    const historyContext = MemoryService.getContextSummary(company.id, project.id);
    
    // Define helper for multi-provider generation (similar to agentRegistry)
    const generateText = async (prompt: string, schema: any) => {
      if (settings.provider === AIProvider.GEMINI) {
        const gemini = new GeminiService(settings.geminiKey);
        // Ensure we don't send legacy model IDs
        const model = (settings.defaultModel.includes('/') || settings.defaultModel.includes(':'))
          ? GeminiModel.FLASH_3
          : settings.defaultModel;
        return await gemini.generateText(prompt, "You are a professional sales copywriter.", model, false, schema);
      } else if (settings.provider === AIProvider.OPENROUTER) {
        let t = '';
        if (settings.costMode === CostMode.FREE_FIRST) {
          t = (await OpenRouterService.generateTextFreeFirst(settings.openRouterKey, prompt, "You are a professional sales copywriter.", settings.defaultModel)).text;
        } else if (settings.costMode === CostMode.CHEAP_PAID_FALLBACK) {
          try {
            t = (await OpenRouterService.generateTextFreeFirst(settings.openRouterKey, prompt, "You are a professional sales copywriter.", settings.defaultModel)).text;
          } catch (error) {
            console.warn('[EmailAgent] Free OpenRouter failed, using paid auto fallback.', error);
            t = await OpenRouterService.generateText(settings.openRouterKey, prompt, "You are a professional sales copywriter.", OpenRouterService.PAID_AUTO_MODEL_ID);
          }
        } else {
          t = await OpenRouterService.generateText(settings.openRouterKey, prompt, "You are a professional sales copywriter.", settings.defaultModel || OpenRouterService.PAID_AUTO_MODEL_ID);
        }
        return { text: t };
      } else {
        const t = await LocalLLMService.generateText(settings.localUrl, prompt, "You are a professional sales copywriter.", settings.defaultModel, settings.localApiKey);
        return { text: t };
      }
    };

    const prompt = `
      Schrijf een ${type === 'initial' ? 'eerste outreachmail' : 'follow-up mail'} voor ${company.name}.
      Projectdoel: "${project.description}"
      Contactpersoon: ${company.contactName || 'contactpersoon'}
      Historiek: ${historyContext || 'Geen eerdere interacties'}
      Taal: Nederlands
      Toon: ${tone === 'casual' ? 'casual maar professioneel' : 'professioneel en overtuigend'}

      Richtlijnen:
      - Gebruik nette e-mailopmaak in het Nederlands.
      - Start met een echte aanhef op een eigen regel, bijvoorbeeld "Beste [naam],".
      - Gebruik maximaal 3 inhoudelijke alinea's met witregels ertussen.
      - Hou iedere alinea compact: maximaal 2 zinnen.
      - Houd de totale mail onder 140 woorden.
      - Voeg een duidelijke call-to-action toe als aparte alinea.
      - Gebruik als afzender dit e-mailadres wanneer beschikbaar: ${buildSenderEmail(project) || 'geen afzender ingesteld'}.
      - Gebruik dit contactblok onder de afsluiting wanneer beschikbaar: ${buildContactBlock(project) || 'geen contactblok ingesteld'}.
      - Eindig met "Met vriendelijke groet," gevolgd door een nette afzender zonder dubbele regels.
      - Gebruik GEEN placeholders zoals [Uw Naam], [Bedrijf], [functie] of vergelijkbaar.
      - Vermijd tekstmuren; lever leesbare alinea's.
      - Return valid JSON only.
    `;

    const schema = {
      type: Type.OBJECT,
      properties: {
        to: { type: Type.STRING },
        subject: { type: Type.STRING },
        body: { type: Type.STRING },
        meta: {
          type: Type.OBJECT,
          properties: {
            language: { type: Type.STRING },
            tone: { type: Type.STRING },
            purpose: { type: Type.STRING }
          }
        }
      },
      required: ["to", "subject", "body"]
    };

    try {
      const response = await generateText(prompt, schema);

      const json = JSON.parse(response.text || "{}");
      return normalizeEmailDraft({
          to: json.to || company.email || "",
          subject: json.subject || "Follow up regarding our previous conversation",
          body: json.body || "I wanted to follow up...",
          meta: {
              language: json.meta?.language || "nl",
              tone: json.meta?.tone || tone,
              purpose: json.meta?.purpose || type
          }
      }, project, company, type, tone);
    } catch (e) {
      return normalizeEmailDraft({
        to: company.email || "",
        subject: "Drafting Failed",
        body: "Beste contactpersoon,\n\nDe AI kon op dit moment geen e-mail opstellen. Probeer het opnieuw.\n\nMet vriendelijke groet,\nBDAT",
        meta: { language: "nl", tone, purpose: type }
      }, project, company, type, tone);
    }
  }

  static async sendEmail(project: Project, company: Company, draft: EmailDraft) {
    const settings = SettingsService.getSettings();
    let finalTo = draft.to;
    let finalSubject = draft.subject;
    let logSummary = `Sent ${draft.meta?.purpose || 'email'}: "${draft.subject}"`;

    // --- TEST MODE INTERCEPTION ---
    if (settings.appMode === 'test') {
        if (!settings.testEmailAddress) {
            console.error("TEST MODE ERROR: No test email configured.");
            alert("LET OP: Test E-mailadres is niet ingesteld in instellingen! Email wordt niet verzonden.");
            return false;
        }
        finalTo = settings.testEmailAddress;
        finalSubject = `[TEST MODE - Original: ${company.email}] ${draft.subject}`;
        logSummary = `[TEST] Sent email to ${finalTo} (Simulating send to ${company.email})`;
        
        // Simuleer een succesvolle verzending naar de test inbox
        console.log(`Sending TEST email to ${finalTo}`);
    }

    // TODO: In LIVE mode hier de echte API integratie (SendGrid/Gmail) toevoegen
    
    const interaction: Interaction = {
      id: crypto.randomUUID(),
      type: 'email',
      direction: 'outbound',
      timestamp: new Date().toISOString(),
      summary: logSummary,
      sentiment: 'neutral',
      details: { ...draft, to: finalTo, subject: finalSubject } // Save the actual sent details
    };
    MemoryService.addInteraction(company.id, project.id, interaction);
    MemoryService.addTask({
        id: crypto.randomUUID(),
        projectId: project.id,
        companyId: company.id,
        title: `Check reply for ${company.name}`,
        dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'follow-up',
        status: 'pending'
    });
    return true;
  }
}
